import {
  Box,
  Button,
  ButtonVariants,
  Dialog,
  DialogFooterWrapper,
  FormControlSizes,
  HStack,
  Icon,
  IconButton,
  IconName,
  IndicatorBadge,
  IndicatorBadgeSizes,
  IndicatorBadgeVariants,
  MixpanelEvent,
  MixpanelEventSource,
  NavButton,
  Stack,
  Text,
  useMixpanel,
} from '@talos/kyoko';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { useTheme } from 'styled-components';
import { CutoutOverlay } from './CutoutOverlay';
import { getShowcaseStepImageUrl, prefetchShowcaseImages } from './prefetchShowcaseImages';
import {
  ShowcaseDescription,
  ShowcaseImage,
  ShowcaseImageCaption,
  ShowcaseTitle,
  StepDot,
  StepIndicator,
} from './styles';
import type { ShowcaseStep } from './types';

export interface ShowcaseProps {
  isOpen: boolean;
  close: () => void;
  steps: ShowcaseStep[];
  /** Called when the showcase is completed */
  onComplete?: () => void;
  /** The current step index within the active showcase */
  currentStep: number;
  /** Go to the next step in the current showcase */
  onNext: () => void;
  /** Go to the previous step in the current showcase */
  onPrev: () => void;
  /** Go to a specific step */
  onStepChange: (step: number) => void;
  /** Override the width of the showcase */
  width?: string;
  /** Override the height of the showcase */
  height?: string;
}

export function Showcase({
  isOpen,
  close,
  steps,
  currentStep,
  onNext,
  onPrev,
  onStepChange,
  onComplete,
  width = '800px',
  height = '600px',
}: ShowcaseProps) {
  const { type } = useTheme();
  const hasMultipleSteps = steps.length > 1;
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;
  const step = steps.at(currentStep);
  const learnMoreUrl = step?.learnMoreUrl;
  const heightRef = useRef<number>(0);
  const dialogRef = useRef<HTMLDivElement>(null);
  const mixpanel = useMixpanel();

  useLayoutEffect(() => {
    if (isOpen) {
      heightRef.current = dialogRef.current?.clientHeight ?? 0;
    } else {
      heightRef.current = 0;
    }
  }, [isOpen]);

  // Reset step when modal closes
  useEffect(() => {
    if (!isOpen) {
      onStepChange(0);
    }
  }, [isOpen, onStepChange]);

  // Prefetch step images for the active theme when the showcase opens so later steps do not wait on load
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    prefetchShowcaseImages(steps, type);
  }, [isOpen, steps, type]);

  const stepTitle = step?.title;

  const handleClose = useCallback(() => {
    onStepChange(0);
    close();
    mixpanel.track(MixpanelEvent.CloseFeatureShowcase, { step: currentStep, Title: stepTitle });
  }, [close, onStepChange, mixpanel, currentStep, stepTitle]);

  const handleNext = useCallback(() => {
    if (isLastStep) {
      mixpanel.track(MixpanelEvent.CompleteFeatureShowcase, { Title: stepTitle });
      onComplete?.();
    } else {
      mixpanel.track(MixpanelEvent.NextFeatureShowcaseStep, { step: currentStep, Title: stepTitle });
      onNext();
    }
  }, [isLastStep, onComplete, onNext, currentStep, mixpanel, stepTitle]);

  const handlePrev = useCallback(() => {
    if (!isFirstStep) {
      mixpanel.track(MixpanelEvent.PreviousFeatureShowcaseStep, { step: currentStep, Title: stepTitle });
      onPrev();
    }
  }, [isFirstStep, onPrev, currentStep, mixpanel, stepTitle]);

  const handleStepClick = useCallback(
    (stepIndex: number) => {
      mixpanel.track(MixpanelEvent.ClickFeatureShowcaseStep, { step: stepIndex, Title: steps.at(stepIndex)?.title });
      onStepChange(stepIndex);
    },
    [onStepChange, mixpanel, steps]
  );

  if (!step) {
    return null;
  }

  return (
    <CutoutOverlay targetSelector={step?.targetSelector}>
      <Dialog
        dataTestId="showcase"
        useTransparentOverlay={true}
        ref={dialogRef}
        isOpen={isOpen}
        close={handleClose}
        width={width}
        minHeight={heightRef.current || height}
        alignContent="left"
        headerIcon={IconName.Newspaper}
        showClose={true}
        title={
          <HStack gap="spacing4" data-testid="showcase-header">
            <Text color="colorTextDefault" data-testid="showcase-whats-new">
              What&apos;s New
            </Text>
            <Icon color="colorTextDefault" icon={IconName.ChevronRight} />
            <Text data-testid="showcase-step-title">{step.title}</Text>
          </HStack>
        }
        customActions={
          <DialogFooterWrapper>
            <Box>
              {learnMoreUrl && (
                <NavButton
                  to={learnMoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={IconName.ExternalLink}
                  data-testid="showcase-learn-more"
                  onClick={() => {
                    mixpanel.track(MixpanelEvent.KnowledgeBase);
                    mixpanel.track(MixpanelEvent.OpenKnowledgeBase, {
                      source: MixpanelEventSource.FeatureShowcase,
                      step: currentStep,
                      Title: step.title,
                    });
                  }}
                >
                  Learn More
                </NavButton>
              )}
            </Box>

            <HStack gap="spacing12" ml="auto" alignItems="center">
              {hasMultipleSteps && (
                <>
                  <IconButton
                    icon={IconName.ChevronLeft}
                    onClick={handlePrev}
                    disabled={isFirstStep}
                    data-testid="showcase-prev-button"
                  />
                  <StepIndicator data-testid="showcase-step-indicator">
                    {steps.map((_, index) => (
                      <StepDot
                        key={index}
                        active={index === currentStep}
                        visited={index < currentStep}
                        onClick={() => handleStepClick(index)}
                        aria-label={`Go to step ${index + 1}`}
                        data-testid="showcase-step-dot"
                      />
                    ))}
                  </StepIndicator>
                </>
              )}
              <Button
                variant={ButtonVariants.Primary}
                size={FormControlSizes.Default}
                onClick={handleNext}
                data-testid="showcase-save-button"
                // Fixed width to avoid layout shift when the button text changes
                width="76px"
              >
                {isLastStep ? 'Confirm' : 'Next'}
              </Button>
            </HStack>
          </DialogFooterWrapper>
        }
      >
        <Stack gap="spacing4" flex={1}>
          <div>
            <IndicatorBadge
              variant={IndicatorBadgeVariants.Notification}
              size={IndicatorBadgeSizes.Large}
              data-testid="showcase-new-badge"
            >
              New
            </IndicatorBadge>
          </div>
          <ShowcaseTitle data-testid="showcase-title">{step.title}</ShowcaseTitle>
          <ShowcaseDescription data-testid="showcase-description">
            <ReactMarkdown>{step.description}</ReactMarkdown>
          </ShowcaseDescription>
          {step?.imageDark && step?.imageLight && (
            <>
              <ShowcaseImage src={getShowcaseStepImageUrl(step, type)} alt={step.title} data-testid="showcase-image" />
              {step.imageCaption && (
                <ShowcaseImageCaption data-testid="showcase-image-caption">{step.imageCaption}</ShowcaseImageCaption>
              )}
            </>
          )}
          {step.customRenderer && <CustomRendererWrapper step={step} renderer={step.customRenderer} />}
        </Stack>
      </Dialog>
    </CutoutOverlay>
  );
}

function CustomRendererWrapper({
  step,
  renderer,
}: {
  step: ShowcaseStep;
  renderer: (step: ShowcaseStep) => React.ReactNode;
}) {
  return <div data-testid="showcase-custom-content">{renderer(step)}</div>;
}
